/**
 * Phase 4: Gateway Health Utility
 * Ensures the backend is reachable before initiating heavy spatial AI pipelines.
 * SMART BRIDGE: Probes multiple ports if the primary one is unreachable.
 */
export async function checkGatewayHealth(): Promise<boolean> {
    const primaryUrl = (import.meta as any).env.VITE_GATEWAY_URL || 'http://127.0.0.1:3002';
    const isProd = (import.meta as any).env.VITE_PROD_MODE === 'true';
    
    // In Pro Mode, we only trust the primary production gateway.
    const fallbackPorts = isProd ? [] : [3000, 3001, 3002, 3003];

    // 🕒 PERFORMANCE: 10s timeout
    const PROBE_TIMEOUT = 10000;
    
    // 🛡️ AUTHORITY GUARD: In development, we MUST talk to the local backend 
    // to ensure the New Frontend and New Backend stay in sync. 
    // Bypassing the Cloud URL prevents legacy logic from leaking in.
    // 🛡️ AUTHORITY GUARD: Strictly follow routing discipline.
    // Probing is now limited to the configured gateway to ensure zero drift.
    let urlsToProbe = [primaryUrl];
    
    if (!isProd) {
        console.info("%c[SmartBridge] DEV MODE: Connectivity restricted to Localhost.", "color: #f59e0b; font-weight: bold;");
    } else {
        console.info("%c[SmartBridge] PROD MODE: Connectivity restricted to Cloud Gateway.", "color: #22c55e; font-weight: bold;");
    }

    console.info("[SmartBridge] Starting discovery...", { urlsToProbe });


    for (const baseUrl of urlsToProbe) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT); 
            
            try {
                const probeUrl = `${baseUrl.replace(/\/$/, '')}/v1/health`;
                const response = await fetch(probeUrl, { 
                    signal: controller.signal,
                    // 🛡️ SECURITY: Explicitly allow private network access for local discovery
                    // Note: Browsers may still require the server header
                    mode: 'cors'
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.status && data.status.toLowerCase() === "ok") {
                        console.info(`[SmartBridge] SUCCESS: Backend discovered at ${baseUrl}`);
                        (window as any).__WINGMAN_ACTIVE_GATEWAY = baseUrl;
                        return true;
                    }
                }
            } finally {
                clearTimeout(timeoutId);
            }
        } catch (error: any) {
            console.warn(`[SmartBridge] Probe failed for ${baseUrl}: ${error.message}`);
        }
    }
    
    console.error("[SmartBridge] CRITICAL: No reachable backend found. Falling back to local autonomous mode.");
    return false;
}